// Páginas institucionales del SPEC MASTER v4 — datos verificados.
// Una sola exportación con tres componentes para mantener fácil expansión.

import { ReactNode } from "react";
import { BannerFuentes } from "@/components/BannerFuentes";

type Norma = { norma: string; descripcion: string; url?: string };


// ====================================================================
// RENIEC · Padrón y Normativa
// ====================================================================

const RENIEC_NORMATIVA: Norma[] = [
  { norma: "D.S. N° 039-2025-PCM", descripcion: "Convocatoria EG2026", url: "https://www.gob.pe/institucion/pcm/normas-legales/" },
  { norma: "RJ N° 000030-2026/JNAC/RENIEC", descripcion: "Extiende vigencia DNI vencidos para sufragio EG2026", url: "https://identidad.reniec.gob.pe/elecciones-generales-2026" },
  { norma: "RJ N° 000039-2026/JNAC/RENIEC", descripcion: "Admite DNI de menor de edad para sufragio EG2026" },
  { norma: "RJ N° 000042-2026/JNAC/RENIEC", descripcion: "Dispone cierre del Padrón Electoral", url: "https://proyectozero24.com/wp-content/uploads/2026/04/RENIEC.-Disponen-Cierre-Padron-Electoral.-Elecciones-2026.pdf" },
  { norma: "Ley N° 32264", descripcion: "Modifica art. 201 y 203 LOE (cierre 180 días antes)" },
  { norma: "Resolución N° 0632-2025-JNE", descripcion: "Aprueba cronograma electoral EG2026", url: "https://portal.jne.gob.pe/portal/Pagina/Ver/979/page/Elecciones-Generales-2026" },
];

export function ReniecPage() {
  return (
    <PageShell titulo="RENIEC" subtitulo="Padrón Electoral EG2026 + Normativa">
      <Chips chips={[
        { txt: "🟢 Datos oficiales", cls: "chip-probado" },
        { txt: "EG2026", cls: "chip-reconocido" },
      ]} />

      <SectionH2>Cifras canónicas del padrón EG2026</SectionH2>
      <KpiGrid items={[
        { label: "Padrón total", value: "27,325,432", hint: "Perú + Extranjero · oficial ONPE" },
        { label: "Padrón Perú", value: "26,114,619", hint: "domicilio en territorio nacional" },
        { label: "Padrón Extranjero", value: "1,210,813", hint: "peruanos en el exterior" },
        { label: "Mesas instaladas", value: "92,766", hint: "incluye 4,703 mesas 900K (CCPP)" },
      ]} />
      <p className="mt-4 text-sm text-ink-300">
        Fuente única: <code className="text-ink-200">electoral_commons.mxm_master</code> (PRESI-NEW · SAAE).
        Cifras parciales históricas (27,201,975 padrón inicial / 27,143,912 parser mivotoimporta)
        están registradas pero no son canónicas.
      </p>

      <SectionH2>Hitos del cierre del padrón</SectionH2>
      <ul className="space-y-2 text-sm text-ink-200">
        <li><strong className="text-ink-50">2025-10-14</strong> — Cierre inicial padrón electoral.</li>
        <li><strong className="text-ink-50">2025-10-23</strong> — Entrega al JNE.</li>
        <li><strong className="text-ink-50">2025-10-27 al 2025-10-31</strong> — Publicación del padrón.</li>
        <li><strong className="text-ink-50">2026-04-07</strong> — Cierre definitivo (RJ N° 000042-2026/JNAC/RENIEC).</li>
        <li><strong className="text-ink-50">Simulacros:</strong> Iquitos (5-mar-2026), Cusco (1-abr-2026).</li>
      </ul>

      <SectionH2>Marco legal</SectionH2>
      <NormativaTable normas={RENIEC_NORMATIVA} />

      <SectionH2>Portales oficiales</SectionH2>
      <ul className="space-y-1 text-sm">
        <li><EnlaceExt href="https://identidad.reniec.gob.pe/elecciones-generales-2026">Portal principal EG2026</EnlaceExt></li>
        <li><EnlaceExt href="https://sisbi.reniec.gob.pe/PLPI">Consulta de padrón</EnlaceExt></li>
        <li><EnlaceExt href="https://gestdocinterop.reniec.gob.pe/verificadoc/index.htm">Verificar documentos</EnlaceExt></li>
        <li><EnlaceExt href="https://www.reniec.gob.pe/portal/marcoLegal.htm">Marco legal RENIEC</EnlaceExt></li>
      </ul>
    </PageShell>
  );
}

// ====================================================================
// ONPE · Regulación, STAE, Operaciones
// ====================================================================

export function OnpePage() {
  return (
    <PageShell titulo="ONPE" subtitulo="Regulación · STAE · Operaciones EG2026">
      <Chips chips={[
        { txt: "🟢 Datos oficiales", cls: "chip-probado" },
        { txt: "🔴 Contradicciones documentadas", cls: "chip-contradiccion" },
      ]} />
      <BannerFuentes pagina="/onpe" />

      <SectionH2>STAE — Sistema Tecnológico de Apoyo a la Escrutinio</SectionH2>
      <p className="text-ink-200">
        Universo Lima Metro + Callao: <strong className="text-ink-50">29,266 mesas</strong> (2,004 + 265 locales · 8.68M electores).
      </p>
      <KpiGrid items={[
        { label: "Mesas CON STAE", value: "26,695", hint: "91.22%" },
        { label: "Mesas SIN STAE", value: "2,571", hint: "8.78% → acta manual" },
        { label: "Incidencias totales", value: "1,873", hint: "Defensoría Informe 274" },
        { label: "% incidencias STAE", value: "61.2%", hint: "1,147 fallas STAE / 1,873" },
      ]} />
      <div className="mt-4 rounded-lg border border-sem-contradiccion/30 bg-sem-contradiccion/5 p-4 text-sm text-ink-200">
        <strong className="text-sem-contradiccion">🔴 STAE no se usará en 2da vuelta.</strong> Confirmado por ONPE el 2026-05-05 en Comisión de Constitución del Congreso. Auditoría contratada con M&T Corporation del Perú (contrato 067-2025-OMP del 4-nov-2025) bajo normas ISO 27001, ISO 54001, NIST CSF, OWASP.
      </div>

      <SectionH2>Caso Gálaga — empresa logística</SectionH2>
      <p className="text-sm text-ink-200">
        Servicios Generales Gálaga S.A.C. (representante: Juan Charles Alvarado Pfuyo) ganó el contrato principal
        por <strong className="text-ink-50">S/ 6,368,332.75</strong> para transporte, despliegue y repliegue del material
        electoral EG2026, pese a tres penalidades previas ONPE y una alerta del consorcio AFE (oferta ~S/500K menor)
        fechada 2026-02-11, archivada por el ex jefe ONPE Piero Corvetto.
      </p>
      <p className="mt-2 text-sm text-ink-300">
        Investigación judicial en curso — 7 investigados, 12 allanamientos simultáneos el 24-abr-2026.
        Para detalle completo ver{" "}
        <a className="text-gold-300 hover:underline" href="/caso-galaga">/caso-galaga</a>.
      </p>

      <SectionH2>Plan Operativo Electoral v03</SectionH2>
      <p className="text-sm text-ink-200">
        126 ODPEs · 300 electores/mesa · mesas especiales previstas · ficha técnica v04.
      </p>
      <ul className="mt-2 text-sm">
        <li><EnlaceExt href="https://www.onpe.gob.pe/modMarco-Legal/POE-EG-2026-V03.pdf">POE EG-2026 v03 (PDF)</EnlaceExt></li>
      </ul>
    </PageShell>
  );
}

// ====================================================================
// JNE · Regulación, Acuerdo 23-abr, Sesión Congreso 5-may
// ====================================================================

const ANALISIS_ACUERDO_JNE = [
  {
    tema: "Elecciones complementarias",
    posicion: "Inviables — no previstas en LOE para EG",
    critica: "Argumento Silva Huallanca: Ley 28.440 / analogía municipal no aplica a EG",
    estado: "🔴 Contradicción documentada entre ONPE (Ley 28.440) y ámbito real de la ley",
    cat: "contradiccion",
  },
  {
    tema: "Ausentismo y su impacto",
    posicion: "Multicausal, 0.86pp por hora de retraso — impacto 'leve'",
    critica: "JNE midió ausentismo; JFT mide distribución del voto — variables distintas. Δ RLA mesas tardías: +7.85pp",
    estado: "🔴 JNE no midió la variable correcta",
    cat: "contradiccion",
  },
  {
    tema: "Reparación por jornada 13-abr",
    posicion: "114,545 electores 'mitigados', medidas suficientes",
    critica: "Instalar mesa ≠ participación efectiva. ¿Cuántos de los 55,261 votaron realmente?",
    estado: "⚫ Vacío documental — dato no publicado",
    cat: "vacio",
  },
  {
    tema: "Responsabilidad logística ONPE",
    posicion: "Reconoce deficiencias graves",
    critica: "No cuantifica impacto en resultado. Reconoce responsabilidad pero no la conecta con el voto",
    estado: "🟡 Hipótesis técnica sustentada en datos",
    cat: "hipotesis",
  },
  {
    tema: "Cronograma electoral",
    posicion: "Intangible — TC lo reafirmó",
    critica: "García Belaunde: 'el JNE históricamente toma decisiones al margen de la ley (no contra ella) para garantizar la democracia'",
    estado: "🟡 Debate jurídico abierto",
    cat: "hipotesis",
  },
];

export function JnePage() {
  return (
    <PageShell titulo="JNE" subtitulo="Regulación · Acuerdo 23-abr · Sesión Congreso 5-may">
      <Chips chips={[
        { txt: "🔵 Acuerdo del Pleno reconocido oficialmente", cls: "chip-reconocido" },
        { txt: "🟢 Sesión Congreso transcrita", cls: "chip-probado" },
      ]} />
      <BannerFuentes pagina="/jne" />

      <SectionH2>Acuerdo del Pleno JNE — 23 de abril de 2026</SectionH2>
      <p className="text-ink-200">
        El Pleno declaró por unanimidad <strong className="text-ink-50">inviable</strong> realizar
        elecciones complementarias en el marco de las EG2026, reconociendo <em>graves deficiencias</em>
        en la organización ONPE. Cifras oficiales reconocidas:
      </p>
      <KpiGrid items={[
        { label: "Locales afectados", value: "256", hint: "con retraso de material el 12-abr" },
        { label: "Mesas afectadas", value: "3,605" },
        { label: "Electores impactados", value: "1,071,111" },
        { label: "Locales sin material def.", value: "13", hint: "55,261 electores sin votar el 12/4" },
      ]} />

      <SectionH2>Matriz de evaluación del Acuerdo</SectionH2>
      <div className="space-y-3">
        {ANALISIS_ACUERDO_JNE.map((row) => (
          <div key={row.tema} className="rounded-lg border border-ink-700/60 bg-ink-900/40 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-serif text-lg text-ink-50">{row.tema}</h3>
              <span className={chipClass(row.cat)}>{row.estado}</span>
            </div>
            <div className="mt-2 grid gap-2 text-sm md:grid-cols-2">
              <div>
                <div className="text-xs uppercase tracking-widest text-ink-400">Posición JNE</div>
                <div className="text-ink-200">{row.posicion}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-ink-400">Punto crítico</div>
                <div className="text-ink-200">{row.critica}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <SectionH2>Sesión Comisión de Constitución del Congreso — 5 de mayo 2026</SectionH2>
      <p className="text-ink-200">
        16ª sesión ordinaria · 4h 2m · ONPE confirmó origen 900K (Gerencia de Planificación,
        20-ago-2025) · <strong className="text-sem-contradiccion">JNE (Burneo) NO asistió</strong> ·
        argumento Silva Huallanca: <em>Ley 28.440 no aplica a EG</em> · STAE NO se usa en 2da vuelta ·
        auditoría M&T Corp (ISO 27001) · Muñante denunció &gt;400 actas 900K firmadas por la misma persona.
      </p>
    </PageShell>
  );
}

// ====================================================================
// Helpers internos
// ====================================================================

function PageShell({ titulo, subtitulo, children }: { titulo: string; subtitulo: string; children: ReactNode }) {
  return (
    <div className="container-prose py-12 md:py-16">
      <div className="text-xs uppercase tracking-widest text-gold-400/80">{subtitulo}</div>
      <h1 className="mt-1 font-serif text-4xl text-ink-50 md:text-5xl">{titulo}</h1>
      <div className="mt-6 space-y-6">{children}</div>
    </div>
  );
}

function Chips({ chips }: { chips: Array<{ txt: string; cls: string }> }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map((c, i) => <span key={i} className={c.cls}>{c.txt}</span>)}
    </div>
  );
}

function SectionH2({ children }: { children: ReactNode }) {
  return <h2 className="mt-8 text-xl font-semibold text-ink-50 border-t border-ink-700/40 pt-6">{children}</h2>;
}

function KpiGrid({ items }: { items: Array<{ label: string; value: string; hint?: string }> }) {
  return (
    <div className="mt-3 grid gap-3 md:grid-cols-4">
      {items.map((it) => (
        <div key={it.label} className="rounded-lg border border-ink-700/60 bg-ink-900/60 p-4">
          <div className="text-xs uppercase tracking-widest text-ink-400">{it.label}</div>
          <div className="mt-1 font-mono text-xl tabular-nums text-ink-50">{it.value}</div>
          {it.hint && <div className="mt-1 text-xs text-ink-400">{it.hint}</div>}
        </div>
      ))}
    </div>
  );
}

function NormativaTable({ normas }: { normas: Norma[] }) {
  return (
    <div className="mt-2 overflow-x-auto rounded-lg border border-ink-700/60 bg-ink-900/50">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-ink-700/60 text-left text-xs uppercase tracking-wide text-ink-300">
            <th className="py-2 px-3">Norma</th>
            <th className="px-3">Descripción</th>
            <th className="px-3 w-20"></th>
          </tr>
        </thead>
        <tbody>
          {normas.map((n) => (
            <tr key={n.norma} className="border-b border-ink-700/30">
              <td className="py-2 px-3 text-ink-100 font-mono text-xs">{n.norma}</td>
              <td className="px-3 text-ink-200">{n.descripcion}</td>
              <td className="px-3 text-right">
                {n.url && <EnlaceExt href={n.url}>Ver</EnlaceExt>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EnlaceExt({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-gold-300 hover:underline text-sm">
      {children} →
    </a>
  );
}

function chipClass(cat: string): string {
  return ({
    probado: "chip-probado",
    reconocido: "chip-reconocido",
    hipotesis: "chip-hipotesis",
    alegacion: "chip-alegacion",
    vacio: "chip-vacio",
    contradiccion: "chip-contradiccion",
  } as Record<string, string>)[cat] ?? "chip-vacio";
}
